// Merchant copy for each overlap we can describe honestly. Static per key:
// nothing is interpolated, so a wording fix never has to reach a persisted
// cache row. OVERLAP_COPY_KEYS mirrors src/types/overlap.ts, and
// src/types/overlap.test.ts diff-checks the two lists, because the backend
// cannot import across tsconfig's rootDir ("src").
//
// RULES THESE PARAGRAPHS FOLLOW, enforced in overlap-copy.test.ts:
//
//   1. Never assert that the other app IS sending server-side. Finding its
//      pixel proves it is INSTALLED. Whether it also reports from a server
//      is held in its own settings, and for most of these apps those are not
//      readable from outside. Every paragraph says what we saw, says what we
//      cannot see, and sends the merchant to the one screen that can answer
//      it: their own ad account.
//   2. No firm instruction. No "remove the app", no "skip this
//      destination" as an order. The merchant checks, then decides.
//   3. No statistics. No percentages, no "most stores", no counts of
//      anything we did not measure on THEIR store.
//   4. No em dashes.

import type { OverlapCopyKey } from "./overlap-types.js";

export interface OverlapCopy {
  /** Polaris Banner tone. "info" everywhere: nothing here is an alarm. */
  tone: "info" | "warning";
  title: string;
  /** Rendered as separate <p> elements, in order. No markup inside. */
  paragraphs: readonly string[];
}

export const OVERLAP_COPY: Record<OverlapCopyKey, OverlapCopy> = {
  meta_channel: {
    tone: "info",
    title: "Facebook & Instagram is also installed",
    paragraphs: [
      "We found Shopify's Facebook & Instagram channel on your storefront. That channel can report purchases to Meta from Shopify's servers. Whether yours does is held in a token Shopify hides from the storefront, so we cannot read it.",
      "If it is reporting from a server and Datacrow reports too, Meta receives each order twice under two different event IDs and has no way to merge them. Browser events are not affected.",
      "Check it in Meta Events Manager. Open your pixel under Data Sources, open the Purchase event, and look at where recent events came from. Meta lists browser and server separately. If server events are already arriving, keep one sender.",
    ],
  },

  tiktok_app: {
    tone: "info",
    title: "TikTok's Shopify app is also installed",
    paragraphs: [
      "We found the TikTok app's pixel on your storefront. The same pixel can also receive events through TikTok's Events API, and that setting lives inside TikTok rather than on your storefront, so we cannot read it.",
      "If both are sending, TikTok counts each order twice. It dedupes on event ID, and the two senders do not share one.",
      "Check it in TikTok Events Manager. Open your pixel, then its events, and look at how recent purchases arrived. If server events are already listed, keep one sender.",
    ],
  },

  klaviyo_app: {
    tone: "info",
    title: "Klaviyo's Shopify app is also installed",
    paragraphs: [
      "We found Klaviyo's pixel on your storefront. Klaviyo's Shopify app also pulls orders straight from Shopify, which is how its Placed Order metric usually gets filled. Datacrow sends Placed Order as well, through Klaviyo's API.",
      "The count is the smaller problem. The bigger one is flows: if a flow triggers on Placed Order and the order arrives twice, a customer can get two emails.",
      "Check it in Klaviyo. Open Metrics and look for Placed Order under your Shopify integration. If it is there, Klaviyo already has your purchases. What Datacrow adds is Started Checkout and Placed Order sent from Shopify's order webhook, which still arrive when the browser script does not. If you turn this on, look at what your flows trigger on first.",
    ],
  },

  pinterest_app: {
    tone: "info",
    title: "Pinterest's Shopify app is also installed",
    paragraphs: [
      "We found the Pinterest app's pixel on your storefront. That app sets up Pinterest's browser tag, and on some stores it also sends conversions through Pinterest's own API. Pinterest keeps that token to itself, so neither you nor we can see which one you have.",
      "If both are sending, Pinterest counts each order twice. It dedupes on event ID, and the app's event IDs are not ones Datacrow can match.",
      "Check it in Pinterest Ads Manager. Open Conversions and look at the source on your checkout event. Pinterest labels server events API or CAPI, and browser events Tag. If you see API or CAPI, keep one sender.",
    ],
  },

  google_channel_ads: {
    tone: "info",
    title: "Give Datacrow its own Google Ads conversion action",
    paragraphs: [
      "Shopify's Google & YouTube channel is on this store. It reports purchases to Google Ads from the shopper's browser, through a conversion action of its own. Datacrow reports the same purchases from your store's server, to whichever conversion action you point it at here.",
      "Two conversion actions for one purchase is normal. Google Ads counts each action separately, and your totals and your bidding follow the ones marked Primary.",
      "In Google Ads, open Goals, then Conversions, then Summary. Set the action Datacrow sends to as Primary, and set the channel's purchase action to Secondary. Secondary actions keep reporting, they just stay out of your totals and out of bidding.",
      "One thing to get right: give Datacrow a conversion action of its own. Pointing it at the channel's action ID puts both senders inside one action, and then the Primary and Secondary split above has nothing to separate.",
    ],
  },

  google_channel_ga4: {
    tone: "info",
    title: "Google & YouTube channel is also installed",
    paragraphs: [
      "Shopify's Google & YouTube channel is on this store and sends events to Google Analytics 4 from the shopper's browser. Datacrow sends from your store's server.",
      "Purchases are usually fine. GA4 merges purchase events that carry the same transaction ID, and Datacrow sends your Shopify order ID as that ID. Nothing else merges, so page views, product views, carts and checkouts would arrive from both senders, and those counts (and the conversion rate built on them) would read high in a property the channel also feeds.",
      "Check it after a day. In GA4, open Reports, then Engagement, then Events, and look at add_to_cart and page_view rather than purchases. Purchases are the one number that will look right either way. If the others look roughly doubled, point Datacrow's GA4 destination at a property of its own, or leave it off and keep the channel.",
    ],
  },

  google_app_ads: {
    tone: "info",
    title: "Another Google app is also installed",
    paragraphs: [
      "We found a third-party Google app on your storefront, separate from Shopify's own Google channel. Apps like it can report conversions to Google Ads from the browser, from their own server, or both, and which one you have is set inside that app where we cannot read it.",
      "If it reports from a server and Datacrow reports too, and both point at the same conversion action, Google Ads counts the order twice.",
      "Check it in Google Ads, under Goals, then Conversions, then Summary. Look at which conversion actions already receive purchases, and at the source listed on them. Give Datacrow an action nothing else writes to, or keep one sender.",
    ],
  },

  google_app_ga4: {
    tone: "info",
    title: "Another Google app is also installed",
    paragraphs: [
      "We found a third-party Google app on your storefront that can also send events to Google Analytics 4. Whether it sends from the browser or from its own server is set inside that app, where we cannot read it.",
      "GA4 merges purchase events that share a transaction ID, so purchases are usually fine. Nothing else merges, so page views, product views and carts can arrive twice.",
      "Check it in GA4, under Admin, then Data streams. Look at how many streams feed this property and what is sending to each one. If a stream you did not set up is already reporting purchases, keep one sender for the rest of the events.",
    ],
  },

  microsoft_uet_app: {
    tone: "info",
    title: "A Microsoft UET pixel app is also installed",
    paragraphs: [
      "We found a UET pixel on your storefront, posting to Microsoft's browser endpoint. We cannot tell which app put it there, and we cannot see whether it also reports conversions from a server of its own.",
      "If both senders feed a goal that counts toward Conversions, Microsoft counts each order twice. It merges two reports of one purchase only when both carry the same event ID on the same tag, and a browser tag does not know Datacrow's event IDs. A goal that fires on the thank-you page URL has the same effect.",
      "Check it in Microsoft Advertising. Open Conversion goals and look at what each goal counts. Point one goal at the purchase event Datacrow sends, and set the other so it does not count toward Conversions. The UET tag itself still earns its place for page views and remarketing audiences.",
    ],
  },

  snapchat_app: {
    tone: "info",
    title: "Snapchat's Shopify app is also installed",
    paragraphs: [
      "We found the Snapchat Ads app's pixel on your storefront. Snapchat files that app under its Conversions API and says installing the pixel this way installs the Conversions API with it. What none of that settles is whether server events are switched on for your pixel today, because that state lives inside Snapchat and not on your storefront, so we cannot see it.",
      "If both senders are on, your purchases may still line up. Snapchat matches a browser purchase to a server one when both carry the same order ID, and Datacrow sends your Shopify order number. Whether this app sends the same one, we have not confirmed. Product views, carts and checkouts have no such fallback, so those would arrive twice.",
      "Check it in Events Manager. Open it from the menu in Snapchat Ads Manager and look at the event sources listed for your pixel, which Snapchat lists separately for the pixel and for the Conversions API. If a Conversions API source is already there, keep one sender.",
      "Worth weighing before you change anything: Snapchat's own setup connects your pixel and your product catalog with a single checkbox, and names no separate switch for server events. Taking the app off your store to stop a second sender would stop the catalog sync your dynamic ads run on too. So if you settle on one sender, do it from this page.",
    ],
  },

  reddit_app: {
    tone: "info",
    title: "Reddit's pixel app is also installed",
    paragraphs: [
      "We found Reddit's pixel on your storefront. Whether it also sends conversions from a server of its own is held inside Reddit Ads rather than on your storefront, so we cannot see it from here.",
      "If both senders are on, purchases can count twice. Reddit merges two reports of one purchase only when both carry the same conversion ID, and we cannot see what another app sends as one, so we cannot promise the pair will line up. Product views and carts have no fallback at all.",
      "Check it in Events Manager. Open it in Reddit Ads and look at your pixel's events, where Reddit shows the source alongside each one. If conversions are already arriving from a server, keep one sender.",
    ],
  },
};
